import { create } from 'zustand';
import { Routine, RoutineBlock, SessionBlockLog } from '../types/index.js';

interface RoutineState {
  activeRoutine: Routine | null;
  activeBlockIndex: number;
  blockRemainingSeconds: number;
  isHudActive: boolean;
  isTimerRunning: boolean;
  wakeLockActive: boolean;
  loggedBlocks: SessionBlockLog[];
  blockDeadline: number | null;
  sessionStartedAt: number;
  activeSeconds: number;

  // Actions
  startRoutine: (routine: Routine, scaledDurationMinutes?: number) => void;
  scaleRoutineDuration: (routine: Routine, targetMinutes: number) => Routine;
  restoreHudSession: (routine: Routine) => boolean;
  nextBlock: () => void;
  previousBlock: () => void;
  tickTimer: () => number;
  setIsTimerRunning: (running: boolean) => void;
  setWakeLockActive: (active: boolean) => void;
  logBlockBpm: (blockId: string, bpm: number, category?: string) => void;
  finishHud: () => void;
  exitHud: () => void;
}

const HUD_STORAGE_KEY = 'fb-hud-session';
// Kept in localStorage so a session survives the OS killing the app (Android); a stale one is ignored after this.
const HUD_MAX_AGE_MS = 3 * 60 * 60 * 1000;

export interface PersistedHud {
  routineId: string;
  blockIndex: number;
  remaining: number;
  deadline: number | null;
  sessionStartedAt: number;
  activeSeconds?: number;
  loggedBlocks: SessionBlockLog[];
  savedAt: number;
}

/** Unfinished HUD session saved by the store, or null when none / too old / unreadable. */
export function readPersistedHud(): PersistedHud | null {
  try {
    const raw = localStorage.getItem(HUD_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PersistedHud;
    if (
      typeof p.routineId !== 'string' ||
      typeof p.blockIndex !== 'number' ||
      p.blockIndex < 0 ||
      Date.now() - p.savedAt > HUD_MAX_AGE_MS
    ) {
      localStorage.removeItem(HUD_STORAGE_KEY);
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

export function clearPersistedHud(): void {
  try {
    localStorage.removeItem(HUD_STORAGE_KEY);
  } catch {
    // storage unavailable
  }
}

/**
 * Distribute targetMinutes across blocks proportionally (largest remainder) with a
 * 1-minute floor. When target < block count the floor wins and the sum overshoots.
 */
export function distributeMinutes(durations: number[], targetMinutes: number): number[] {
  const n = durations.length;
  const total = durations.reduce((a, d) => a + d, 0);
  if (n === 0 || total === 0) return durations.slice();
  const ideal = durations.map((d) => (d * targetMinutes) / total);
  const out = ideal.map((x) => Math.max(1, Math.floor(x)));
  if (targetMinutes < n) return out;
  let sum = out.reduce((a, d) => a + d, 0);
  while (sum > targetMinutes) {
    let pick = -1;
    for (let i = 0; i < n; i++) {
      if (out[i] > 1 && (pick === -1 || out[i] - ideal[i] > out[pick] - ideal[pick])) pick = i;
    }
    if (pick === -1) break;
    out[pick]--;
    sum--;
  }
  while (sum < targetMinutes) {
    let pick = 0;
    for (let i = 1; i < n; i++) {
      if (ideal[i] - out[i] > ideal[pick] - out[pick]) pick = i;
    }
    out[pick]++;
    sum++;
  }
  return out;
}

export const useRoutineStore = create<RoutineState>((set, get) => ({
  activeRoutine: null,
  activeBlockIndex: 0,
  blockRemainingSeconds: 0,
  isHudActive: false,
  isTimerRunning: false,
  wakeLockActive: false,
  loggedBlocks: [],
  blockDeadline: null,
  sessionStartedAt: 0,
  activeSeconds: 0,

  scaleRoutineDuration: (routine: Routine, targetMinutes: number) => {
    const nominalMinutes = routine.blocks.reduce((acc, b) => acc + b.durationMinutes, 0);
    if (nominalMinutes === 0 || nominalMinutes === targetMinutes) return routine;

    const minutes = distributeMinutes(routine.blocks.map((x) => x.durationMinutes), targetMinutes);
    const scaledBlocks: RoutineBlock[] = routine.blocks.map((b, i) => ({
      ...b,
      durationMinutes: minutes[i]
    }));

    return {
      ...routine,
      targetDurationMinutes: targetMinutes,
      blocks: scaledBlocks
    };
  },

  startRoutine: (routine, scaledDurationMinutes) => {
    const targetRoutine = scaledDurationMinutes
      ? get().scaleRoutineDuration(routine, scaledDurationMinutes)
      : routine;

    const firstBlockDuration = targetRoutine.blocks[0]?.durationMinutes || 5;

    set({
      activeRoutine: targetRoutine,
      activeBlockIndex: 0,
      blockRemainingSeconds: firstBlockDuration * 60,
      blockDeadline: Date.now() + firstBlockDuration * 60 * 1000,
      sessionStartedAt: Date.now(),
      activeSeconds: 0,
      isHudActive: true,
      isTimerRunning: true,
      loggedBlocks: []
    });
  },

  restoreHudSession: (routine) => {
    try {
      const raw = localStorage.getItem(HUD_STORAGE_KEY);
      if (!raw) return false;
      const p = JSON.parse(raw) as PersistedHud;
      if (
        p.routineId !== routine.id ||
        typeof p.blockIndex !== 'number' ||
        p.blockIndex < 0 ||
        p.blockIndex >= routine.blocks.length ||
        Date.now() - p.savedAt > HUD_MAX_AGE_MS
      ) {
        return false;
      }
      const now = Date.now();
      // Resume PAUSED from the value at the last save; the time away is not counted.
      set({
        activeRoutine: routine,
        activeBlockIndex: p.blockIndex,
        blockRemainingSeconds: Math.max(0, p.remaining),
        blockDeadline: null,
        sessionStartedAt: p.sessionStartedAt || now,
        activeSeconds: p.activeSeconds ?? 0,
        isHudActive: true,
        isTimerRunning: false,
        loggedBlocks: Array.isArray(p.loggedBlocks) ? p.loggedBlocks : []
      });
      return true;
    } catch {
      return false;
    }
  },

  nextBlock: () => {
    const state = get();
    if (!state.activeRoutine) return;

    const nextIndex = state.activeBlockIndex + 1;
    if (nextIndex < state.activeRoutine.blocks.length) {
      const nextDuration = state.activeRoutine.blocks[nextIndex].durationMinutes;
      set({
        activeBlockIndex: nextIndex,
        blockRemainingSeconds: nextDuration * 60,
        blockDeadline: Date.now() + nextDuration * 60 * 1000,
        isTimerRunning: true
      });
    } else {
      // Completed all blocks
      set({ isTimerRunning: false, blockDeadline: null });
    }
  },

  previousBlock: () => {
    const state = get();
    if (!state.activeRoutine || state.activeBlockIndex === 0) return;

    const prevIndex = state.activeBlockIndex - 1;
    const prevBlock = state.activeRoutine.blocks[prevIndex];
    const prevDuration = prevBlock.durationMinutes;
    set({
      activeBlockIndex: prevIndex,
      // The block is being redone: drop its earlier BPM log so the session does not log it twice
      loggedBlocks: state.loggedBlocks.filter((l) => l.blockId !== prevBlock.id),
      blockRemainingSeconds: prevDuration * 60,
      blockDeadline: state.isTimerRunning ? Date.now() + prevDuration * 60 * 1000 : null
    });
  },

  // Wall-clock based: remaining is derived from the block deadline, so throttled or
  // backgrounded tabs catch up. Returns the number of whole seconds that elapsed.
  tickTimer: () => {
    const { blockRemainingSeconds, isTimerRunning, blockDeadline } = get();
    if (!isTimerRunning) return 0;

    if (blockDeadline === null) {
      set({ blockDeadline: Date.now() + blockRemainingSeconds * 1000 });
      return 0;
    }
    const next = Math.max(0, Math.ceil((blockDeadline - Date.now()) / 1000));
    if (next < blockRemainingSeconds) {
      set({ blockRemainingSeconds: next, activeSeconds: get().activeSeconds + (blockRemainingSeconds - next) });
      return blockRemainingSeconds - next;
    }
    return 0;
  },

  setIsTimerRunning: (isTimerRunning) =>
    set((state) => ({
      isTimerRunning,
      blockDeadline: isTimerRunning
        ? state.isTimerRunning && state.blockDeadline !== null
          ? state.blockDeadline
          : Date.now() + state.blockRemainingSeconds * 1000
        : null
    })),

  setWakeLockActive: (wakeLockActive) => set({ wakeLockActive }),

  logBlockBpm: (blockId, achievedBpm, category) => {
    set((state) => ({
      loggedBlocks: [...state.loggedBlocks, { blockId, achievedBpm, category }]
    }));
  },

  finishHud: () => {
    set({ isHudActive: false, isTimerRunning: false, blockDeadline: null });
  },

  exitHud: () => {
    set({
      isHudActive: false,
      blockDeadline: null,
      isTimerRunning: false,
      activeRoutine: null
    });
  }
}));

// Persist HUD progress so a reload or app restart can restore the block index and remaining time.
useRoutineStore.subscribe((state, prev) => {
  try {
    if (state.isHudActive && state.activeRoutine) {
      const p: PersistedHud = {
        routineId: state.activeRoutine.id,
        blockIndex: state.activeBlockIndex,
        remaining: state.blockRemainingSeconds,
        deadline: state.isTimerRunning ? state.blockDeadline : null,
        sessionStartedAt: state.sessionStartedAt,
        activeSeconds: state.activeSeconds,
        loggedBlocks: state.loggedBlocks,
        savedAt: Date.now()
      };
      localStorage.setItem(HUD_STORAGE_KEY, JSON.stringify(p));
    } else if (prev.isHudActive) {
      // Only clear when a running session ends, so an unrelated store change at app start
      // cannot wipe a saved session before the dashboard offers to resume it.
      localStorage.removeItem(HUD_STORAGE_KEY);
    }
  } catch {
    // storage unavailable
  }
});
